/// <amd-module name="Test/TestPlansHubRefresh/TestPlansSegment/Component/TestPlansSegmentConnectedView" />
import { Favorite } from "Favorites/Common/Contracts/Favorite";
import { FavoritesStarButton } from "Favorites/Star/FavoritesStarButton";
import * as React from "react";
import { connect } from "react-redux";
import { Dispatch } from "redux";
import { DynamicModuleLoader } from "redux-dynamic-modules";
import { TelemetryConstants, TestHubRefreshConstants } from "Test/TestPlansHubRefresh/TestHub/Constants";
import { TelemetryActions } from "Test/TestPlansHubRefresh/TestHub/Redux//TelemetryActions";
import { SuitesActions } from "Test/TestPlansHubRefresh/TestHub/Redux/Suites/SuitesActions";
import { ITestHubRefreshLocalContext } from "Test/TestPlansHubRefresh/TestHub/Redux/TestHubRefresh/TestHubRefreshContracts";
import { TestHubRefreshLocalContext } from "Test/TestPlansHubRefresh/TestHub/Utils/TestPlansHubRefreshLocalContextProvider";
import { isAdvancedLicenseEnabled } from "Test/TestPlansHubRefresh/TestHub/Redux/TestHubRefresh/TestHubRefreshSelector";
import { TestPlansSegmentActions } from "Test/TestPlansHubRefresh/TestPlansSegment/Redux/TestPlansSegmentActions";
import { ITestPlansSegmentAwareState } from "Test/TestPlansHubRefresh/TestPlansSegment/Redux/TestPlansSegmentContracts";
import { TestPlansSegmentModule } from "Test/TestPlansHubRefresh/TestPlansSegment/Redux/TestPlansSegmentModule";
import * as Resources from "Test/TestPlansHubRefresh/TestPlansSegment/Resources";
import { DateTimeConstants } from "Test/TestPlansHubRefresh/TestPlansSegment/TestplansSegmentConstants";
import { ObservableArray, ObservableValue } from "VSS/Core/Observable";
import { format, localeIgnoreCaseComparer } from "VSS/Core/Util/String";
import { IVssComponentContext, IVssComponentProperties, VssComponent, WrappedComponent } from "VSS/Platform/Layout";
import { IVssPerformanceService } from "VSS/Platform/Performance";
import { TTIMonitorActions } from "VSS/Redux/Modules/TTIMonitor";
import { Button } from "VSSUI/Button";
import { Dropdown, DropdownExpandableButton } from "VSSUI/Dropdown";
import { CustomHeader, HeaderTitleArea, HeaderTitleRow } from "VSSUI/Header";
import { HeaderCommandBar, IHeaderCommandBarItem } from "VSSUI/HeaderCommandBar";
import { ListSelection } from "VSSUI/List";
import { IListBoxItem, ListBoxItemType } from "VSSUI/ListBox";
import { Observer } from "VSSUI/Observer";
import { Pill, PillSize, PillVariant } from "VSSUI/Pill";
import { Tooltip } from "VSSUI/TooltipEx";
import { IColor } from "VSSUI/Utilities/Color";
import { UnsavedChangesMessage } from "Test/TestPlansHubRefresh/TestHub/Resources";

export interface ITestPlansSegmentViewComponentProps extends IVssComponentProperties {
    testPlanId: number;
    testPlanName: string;
    testPlanStartDate: Date;
    testPlanEndDate: Date;
    isAdvancedLicenseEnabled: boolean;
    mineAllPageUrl: string;
    isFavorited: boolean;
    favoriteId: string;
    favoritePlans: Favorite[];
    testPlanProgressSummary: string;
    testhubRefreshContext?: ITestHubRefreshLocalContext;

    // Required to show unsaved Changes alert to user when in grid view on suite change.
    isInGridView: boolean;
    isGridDirty: boolean;
}

export interface ITestPlansSegmentViewComponentDispatchProps {
    getInitialFavoriteStatusForPlan: (testPlanId: number) => void;
    fetchFavorites: () => void;
    onToggleFavorite: (testPlanId: number, testPlanName: string, favoriteStatus: boolean, favoriteId: string) => void;
    changeTestPlan: (testPlanId: number) => void;
    updateLoadingStatus: (loadingStatus: boolean) => void;
    copyTestPlanClicked: (action: string, properties?: { [key: string]: any }) => void;
    editTestPlan: (planId: number) => void;
    getTestPlanProgressSummary: (planId: number) => void;
    openProgressReport: (planId: number) => void;
    openTestPlanSettingsDialog: (planId: number) => void;
}

export class TestPlansSegmentView extends VssComponent<ITestPlansSegmentViewComponentProps & ITestPlansSegmentViewComponentDispatchProps> {
    constructor(props: ITestPlansSegmentViewComponentProps & ITestPlansSegmentViewComponentDispatchProps, context: IVssComponentContext) {
        super(props);
        this._selection = new ListSelection({ multiSelect: false, selectOnFocus: false });
        this._items = new ObservableArray<IListBoxItem<{}>>();
        this._loading = new ObservableValue<boolean>(false);
        this._onExpandDropdownMenu = this._onExpandDropdownMenu.bind(this);
        this._showPanel = new ObservableValue<boolean>(false);
    }

    private _selection: ListSelection;
    private _items: ObservableArray<IListBoxItem<{}>>;
    private _loading: ObservableValue<boolean>;
    private _showPanel: ObservableValue<boolean>;

    private _onToggleFavorite = (): void => {
        this.props.onToggleFavorite(this.props.testPlanId, this.props.testPlanName, this.props.isFavorited, this.props.favoriteId);
    };

    private _onExpandDropdownMenu = (): void => {
        this._items.removeAll();
        this._loading.value = true;
        this._items.push({ id: "loading", type: ListBoxItemType.Loading });
        this.props.fetchFavorites();
    };

    private _onTestPlanSelection = (planId: string): void => {
        if (!this.props.isInGridView || !this.props.isGridDirty || confirm(UnsavedChangesMessage)) {
            // mark tti for test plan change scenario 
            if (this.props.testhubRefreshContext) {
                const currentPivot: string | undefined = this.props.testhubRefreshContext.getCurrentPivot();

                if (currentPivot) {
                    let scenarioName: string | undefined = undefined;

                    if (localeIgnoreCaseComparer(currentPivot, TestHubRefreshConstants.DEFINE_TAB_ID) === 0) {
                        scenarioName = TelemetryConstants.AUTHOR_PIVOT_PAGE_LOAD_TTI_SCENARIO;
                    } else if (localeIgnoreCaseComparer(currentPivot, TestHubRefreshConstants.EXECUTE_TAB_ID) === 0) {
                        scenarioName = TelemetryConstants.EXECUTE_PIVOT_PAGE_LOAD_TTI_SCENARIO;
                    }

                    if (scenarioName) {
                        const properties = {
                            ttiScenario: TelemetryConstants.TESTPLAN_CHANGE_TTI_SCENARIO
                        };
                        const performanceSerivce: IVssPerformanceService = this.context.pageContext.getService<IVssPerformanceService>(
                            "IVssPerformanceService"
                        );
                        performanceSerivce.startScenario(scenarioName, true, undefined, properties);
                    }
                }
            }

            let testPlanId = Number(planId);
            if (this.props.testPlanId != testPlanId) {
                this.props.changeTestPlan(testPlanId);
                this.props.getTestPlanProgressSummary(testPlanId);
            }
        }
    };

    private _isMinDate(date: Date) {
        //DATETIME_MINDATE_UTC_MS is the UTC time value in ms for the minimum date
        if (date.getTime() === DateTimeConstants.DATETIME_MINDATE_UTC_MS) {
            return true;
        }
        return false;
    }

    private _getLocaleDateString(date: Date) {
        // get month from date using globalization API to handle date localization properly
        return date.toLocaleDateString(DateTimeConstants.Locale, { month: DateTimeConstants.ShortMonthFormat });
    }

    public _onTestPlanSettingsSelection(): void {
        this.props.openTestPlanSettingsDialog(this.props.testPlanId);
    }
    
    public componentDidMount() {
        if (this.props.testPlanId) {
            this.props.getInitialFavoriteStatusForPlan(this.props.testPlanId);
            this.props.getTestPlanProgressSummary(this.props.testPlanId);
        }
    }

    public shouldComponentUpdate(nextProps: ITestPlansSegmentViewComponentProps) {
        if (nextProps.favoritePlans) {
            for (let i = 0; i < nextProps.favoritePlans.length; i++) {
                this._items.push({
                    id: nextProps.favoritePlans[i].artifactId,
                    text: nextProps.favoritePlans[i].artifactName
                });
            }
        }
        if (this._loading.value) {
            this._loading.value = false;
            this._items.removeAll((item) => item.type === ListBoxItemType.Loading);
        }
        return true;
    }

    render(): JSX.Element {
        let elm: JSX.Element = {} as JSX.Element;
        let testPlanProgressSummaryElements: JSX.Element = <></>;
        let startDay: number = -1;
        let endDay: number = -1;
        let formattedInterval: string = "";
        let labelName: string = "";
        let testPlanToolTip: string = "";
        let labelBackgroundColor: IColor = { red: 233, green: 233, blue: 233 };
        // default content class name is for past and future label text
        let labelContentClassName: string = "plan-label-text";
        // This is the default width we use to show the list of favorite plans.
        let width = 300;
        let calloutContentClassName = "";
        let showPlanDatesHeader: boolean = true;

        if (this.props.testPlanStartDate && this.props.testPlanEndDate) {
            if (this._isMinDate(this.props.testPlanStartDate) || this._isMinDate(this.props.testPlanEndDate)) {
                showPlanDatesHeader = false;
            } else {
                startDay = this.props.testPlanStartDate.getDate();
                endDay = this.props.testPlanEndDate.getDate();

                formattedInterval = format(
                    Resources.IterationDateFormat,
                    this._getLocaleDateString(this.props.testPlanStartDate),
                    startDay,
                    this._getLocaleDateString(this.props.testPlanEndDate),
                    endDay
                );
            }
        }

        if (this.props.testPlanName) {
            testPlanToolTip = this.props.testPlanName;
        }
        const currentDate = new Date();
        currentDate.setHours(0, 0, 0, 0);
        if (this.props.testPlanEndDate < currentDate) {
            labelName = Resources.PlanLabelPast;
        }
        //labels are decided on the basis of dates and NOT time if the date is same
        else if (currentDate.setHours(24, 60, 60, 0) && this.props.testPlanStartDate > currentDate) {
            labelName = Resources.PlanLabelFuture;
        } else {
            labelName = Resources.PlanLabelCurrent;
            labelBackgroundColor = { red: 0, green: 120, blue: 215 };
            labelContentClassName = "current-plan-label-text";
        }

        if (!this._items || !this._items.length) {
            // Only set the callout content class name if we dont have any items and need to show the default text.
            calloutContentClassName = "testplan-dropdown-callout-content";
        } else if (this._items.length > 10) {
            // If the number of items in the list is more than 10, the search bar is shown. Adjusting the min-height to show
            // the no search results found text without a vertical scrollbar.
            calloutContentClassName = "testplan-dropdown-search-enabled";
        }

        if (this.props.testPlanProgressSummary) {
            testPlanProgressSummaryElements = (
                <div className="test-plan-progress-report-content">
                    <span id="results-insight">{this.props.testPlanProgressSummary}</span>
                    <span className="progress-report-link" onClick={() => this.props.openProgressReport(this.props.testPlanId)}>
                        {Resources.ViewReport}
                    </span>
                </div>
            );
        }

        elm = (
            <div className="testplans-segment-container">
                <div className="plan-header title-s" aria-level={1}>
                    <CustomHeader>
                        <HeaderTitleArea>
                            <HeaderTitleRow>
                                <Button
                                    className="back-button"
                                    iconProps={{ iconName: "Back" }}
                                    href={this.props.mineAllPageUrl}
                                    subtle={true}
                                    tooltipProps={{ text: Resources.AllTestPlans }}
                                    ariaLabel={Resources.AllTestPlans}
                                />
                                <Dropdown
                                    className="testplan-dropdown-button"
                                    ariaLabel={this.props.testPlanName}
                                    items={this._items}
                                    selection={this._selection}
                                    loading={this._loading}
                                    onSelect={(event: React.SyntheticEvent<HTMLElement>, item: IListBoxItem<{}>) =>
                                        this._onTestPlanSelection(item.id)
                                    }
                                    onExpand={this._onExpandDropdownMenu}
                                    placeholder={this.props.testPlanName}
                                    noItemsText={Resources.NoFavoriteText}
                                    width={width}
                                    calloutContentClassName={calloutContentClassName}
                                    renderExpandable={(props) => (
                                        <DropdownExpandableButton tooltipProps={{ text: testPlanToolTip, overflowOnly: true }} {...props} />
                                    )}
                                />
                                <FavoritesStarButton
                                    className="favorite-button"
                                    isFavorited={this.props.isFavorited}
                                    onToggleFavorite={this._onToggleFavorite}
                                />
                            </HeaderTitleRow>
                            {showPlanDatesHeader ? (
                                <HeaderTitleRow className="plan-dates body-s">
                                    <Tooltip text={Resources.IterationDates}>
                                        <div>{formattedInterval}</div>
                                    </Tooltip>
                                    <Pill
                                        variant={PillVariant.colored}
                                        className="plan-label"
                                        contentClassName={labelContentClassName}
                                        excludeTabStop={true}
                                        size={PillSize.regular}
                                        color={labelBackgroundColor}
                                    >
                                        {labelName}
                                    </Pill>
                                </HeaderTitleRow>
                            ) : null}
                            <HeaderTitleRow className="test-plan-progress-report-container body-s">{testPlanProgressSummaryElements}</HeaderTitleRow>
                        </HeaderTitleArea>
                        <Tooltip text={Resources.MoreOptions}>
                            <div>
                                {" "}
                                <HeaderCommandBar className="setting-menu-icon" items={this.getTestPlanCommand()} />{" "}
                            </div>
                        </Tooltip>
                    </CustomHeader>
                    <div className="separator" />
                </div>
            </div>
        );

        return (
            <div className="test-plan-segment">
                <DynamicModuleLoader modules={[TestPlansSegmentModule]}>{elm}</DynamicModuleLoader>
                <Observer showPanel={this._showPanel}>
                    {(props: { showPanel: boolean }) => {
                        return (
                            props.showPanel && (
                                <WrappedComponent
                                    dependencies={["ms.vss-test-web.testplan-clone-panel"]}
                                    wrappedType="clone-test-plan-panel"
                                    wrappedProps={{
                                        showPanel: this._showPanel
                                    }}
                                />
                            )
                        );
                    }}
                </Observer>
            </div>
        );
    }

    public getTestPlanCommand(): IHeaderCommandBarItem[] {
        if (this.props.isAdvancedLicenseEnabled) {
            return [
                {
                    iconProps: {
                        iconName: "Copy"
                    },
                    id: "CopyPlan",
                    important: false,
                    onActivate: () => {
                        const telemetryPayload = {
                            planId: this.props.testPlanId,
                            testPlanName: this.props.testPlanName,
                            isAdvancedLicenseEnabled: this.props.isAdvancedLicenseEnabled
                        };
                        this.props.copyTestPlanClicked(TelemetryConstants.CLICK_COPY_TEST_PLAN_OPTION, telemetryPayload);
                        this._showPanel.value = true;
                    },
                    text: Resources.CopyTestPlan
                },
                {
                    iconProps: {
                        iconName: "Edit"
                    },
                    id: "EditPlan",
                    important: false,
                    onActivate: () => {
                        this.props.editTestPlan(this.props.testPlanId);
                    },
                    text: Resources.EditTestPlan
                },
                {
                    iconProps: {
                        iconName: "Settings"
                    },
                    id: "TestPlanSettings",
                    important: false,
                    onActivate: () => {
                        this._onTestPlanSettingsSelection();
                    },
                    text: Resources.TestPlanSettings
                }
            ];
        } else {
            return [];
        }
    }
}

function mapDispatchToProps(dispatch: Dispatch): ITestPlansSegmentViewComponentDispatchProps {
    return {
        getInitialFavoriteStatusForPlan: (testPlanId: number) => dispatch(TestPlansSegmentActions.getInitialFavoriteStatusForPlan(testPlanId)),
        onToggleFavorite: (testPlanId: number, testPlanName: string, favoriteStatus: boolean, favoriteId: string) =>
            dispatch(TestPlansSegmentActions.onToggleFavorite(testPlanId, testPlanName, favoriteStatus, favoriteId)),
        fetchFavorites: () => dispatch(TestPlansSegmentActions.fetchFavorites()),
        changeTestPlan: (testPlanId: number) => dispatch(TestPlansSegmentActions.changeTestPlan(testPlanId)),
        updateLoadingStatus: (loadingStatus) => dispatch(SuitesActions.updateLoadingStatus(loadingStatus)),
        copyTestPlanClicked: (action: string, properties?: { [key: string]: any }) => dispatch(TelemetryActions.publishTelemetry(action, properties)),
        editTestPlan: (planId: number) => dispatch(TestPlansSegmentActions.editTestPlan(planId)),
        getTestPlanProgressSummary: (planId: number) => dispatch(TestPlansSegmentActions.getTestPlanProgressSummary(planId)),
        openProgressReport: (planId: number) => dispatch(TestPlansSegmentActions.openProgressReport(planId)),
        openTestPlanSettingsDialog: (planId: number) => dispatch(TestPlansSegmentActions.openTestPlanSettingsDialog(planId))
    };
}

function mapStateToProps(state: ITestPlansSegmentAwareState): Partial<ITestPlansSegmentViewComponentProps> {
    let isFavorited, favoriteId, favoritePlans, testPlanProgressSummary;

    if (state.testPlansSegmentState) {
        if (state.testPlansSegmentState.isFavorited) {
            isFavorited = state.testPlansSegmentState.isFavorited;
            favoriteId = state.testPlansSegmentState.favoriteId;
        }
        if (state.testPlansSegmentState.favoritePlans) {
            favoritePlans = state.testPlansSegmentState.favoritePlans;
        }
        if (state.testPlansSegmentState.testPlanProgressSummary) {
            testPlanProgressSummary = state.testPlansSegmentState.testPlanProgressSummary;
        }
    }

    return {
        testPlanId: state.testHubRefreshState.testPlanId,
        testPlanName: state.testHubRefreshState.testPlanName,
        testPlanStartDate: state.testHubRefreshState.testPlanStartDate,
        testPlanEndDate: state.testHubRefreshState.testPlanEndDate,
        isAdvancedLicenseEnabled: isAdvancedLicenseEnabled(state),
        mineAllPageUrl: state.testHubRefreshState.mineAllPageUrl,
        isFavorited: isFavorited,
        favoriteId: favoriteId,
        favoritePlans: favoritePlans,
        testPlanProgressSummary: testPlanProgressSummary,
        isInGridView: state.testHubRefreshState.isInGridView,
        isGridDirty: state.testHubRefreshState.isGridDirty
    };
}
export function TestPlanSegmentFunctionalComponent(props: ITestPlansSegmentViewComponentProps & ITestPlansSegmentViewComponentDispatchProps): JSX.Element {
    const testhubRefreshLocalContext = React.useContext(TestHubRefreshLocalContext);

    return <TestPlansSegmentView {...props} testhubRefreshContext={testhubRefreshLocalContext} />;
}
export const TestPlansSegmentConnectedView = connect(mapStateToProps, mapDispatchToProps)(TestPlanSegmentFunctionalComponent);

VssComponent.register("testplans-segment-component", TestPlansSegmentConnectedView as ((props: ITestPlansSegmentViewComponentProps & ITestPlansSegmentViewComponentDispatchProps) => JSX.Element));
